import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { beginView, endView, trackFeedback } from '../api/feedback';
import type { Recommendation, Source } from '../api/types';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { Dial } from './Dial';
import { titleLink } from './links';
import type { Line, StationStop } from './lines';
import { Scenery } from './Scenery';
import { Wordmark } from './Wordmark';
import { loadScene, prefetchScene, type Scene } from './scenes';
import { Ticket } from './Ticket';

interface Props {
  stop: StationStop;
  /** The rider's whole network, for the route dots and the departure bar. */
  network: StationStop[];
  index: number;
  onTravel: (to: StationStop, dir: 'fwd' | 'back') => void;
  onMap: () => void;
  /** Where the rider's list came from; the 詳細 button links to the same site. */
  source?: Source;
}

/**
 * The ticket is the page, so it scales down to fit a short window rather than
 * scrolling off one. Ported from the concept's `fitTicket()`: measure the
 * housing's chrome (LED, bay label, slot, console, padding), give the ticket
 * whatever is left, and floor it so it never shrinks to illegibility. The floor
 * is a CSS variable, `--fit-floor`, because how small is too small is a layout
 * question and short viewports answer it differently.
 */
function useFitTicket(deps: unknown[]) {
  const hall = useRef<HTMLElement>(null);
  const machine = useRef<HTMLDivElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);

  const fit = useCallback(() => {
    const h = hall.current;
    const m = machine.current;
    const hold = holder.current;
    const st = stage.current;
    const ticket = st?.querySelector<HTMLElement>('.ticket');
    if (!h || !m || !hold || !st || !ticket) return;

    st.style.setProperty('--s', '1');
    hold.style.height = 'auto';
    const natural = ticket.offsetHeight;
    const chrome = m.offsetHeight - hold.offsetHeight;
    // Measured against the viewport, not the hall: the hall grows when its
    // content overflows, which would make the ticket look like it fits.
    const cs = getComputedStyle(h);
    const hallTop = h.getBoundingClientRect().top + window.scrollY;
    const room =
      window.innerHeight - hallTop - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    // The floor lives in CSS (styles/live.css) so short windows can relax it.
    const floor = Number.parseFloat(cs.getPropertyValue('--fit-floor')) || 0.58;
    // Rounded down, with a pixel spare: offsetHeight rounds, and the holder's
    // height rounds up, so an exact fit can come out a pixel over and scroll.
    const scale = Math.max(floor, Math.min(1, Math.floor(((room - chrome - 1) / natural) * 1000) / 1000));
    st.style.setProperty('--s', scale.toFixed(3));
    // The slot above reads it too, so it stays the width of what it printed.
    m.style.setProperty('--s', scale.toFixed(3));
    hold.style.height = `${Math.ceil(natural * scale)}px`;
  }, []);

  useLayoutEffect(fit, [fit, ...deps]);

  useEffect(() => {
    let pending = 0;
    const onResize = () => {
      window.clearTimeout(pending);
      pending = window.setTimeout(fit, 90);
    };
    window.addEventListener('resize', onResize);
    // Webfonts land after first paint and change the ticket's height.
    if (document.fonts?.ready) void document.fonts.ready.then(fit);
    return () => {
      window.clearTimeout(pending);
      window.removeEventListener('resize', onResize);
    };
  }, [fit]);

  return { hall, machine, holder, stage };
}

export function Station({ stop, network, index, onTravel, onMap, source = 'anilist' }: Props) {
  const { line, picks } = stop;
  const reduced = usePrefersReducedMotion();

  const [scene, setScene] = useState<Scene | null>(null);
  useEffect(() => {
    let live = true;
    void loadScene(line.slug).then((s) => {
      if (live) setScene(s);
    });
    return () => {
      live = false;
    };
  }, [line.slug]);

  const [cur, setCur] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0]));
  const swap = useRef(0);

  // A new station is a fresh platform: back to the first ticket.
  useEffect(() => {
    setCur(0);
    setSeen(new Set([0]));
    setLeaving(false);
  }, [line.slug]);

  const refs = useFitTicket([cur, line.slug, picks.length]);

  const go = useCallback(
    (next: number) => {
      const i = (next + picks.length) % picks.length;
      if (i === cur) return;
      setSeen((s) => new Set(s).add(i));
      if (reduced) {
        setCur(i);
        return;
      }
      setLeaving(true);
      window.clearTimeout(swap.current);
      swap.current = window.setTimeout(() => {
        setCur(i);
        setLeaving(false);
      }, 150);
    },
    [cur, picks.length, reduced],
  );

  useEffect(() => () => window.clearTimeout(swap.current), []);

  const prevStop = network.length > 1 ? (network[(index - 1 + network.length) % network.length] ?? null) : null;
  const nextStop = network.length > 1 ? (network[(index + 1) % network.length] ?? null) : null;

  // The next backdrop is 40–140 KB; fetch it while the rider is still reading.
  useEffect(() => {
    if (nextStop) prefetchScene(nextStop.line.slug);
    if (prevStop) prefetchScene(prevStop.line.slug);
  }, [nextStop, prevStop]);

  // NEXT on the last ticket rides on rather than looping back to the first.
  // Having passed on every title at this platform, the next thing a rider wants
  // is the next platform, not the one they just turned down. The arrow keys and
  // swipes still wrap: those are for walking the platform, and walking off the end of
  // it should not move you to another station by surprise.
  const lastAtPlatform = cur === picks.length - 1;
  const skipDeparts = lastAtPlatform && nextStop !== null;
  const skip = useCallback(() => {
    const r = picks[cur];
    if (r) {
      trackFeedback({ type: 'skip', media_id: r.media_id, rank: r.rank, line: line.slug,
        position: cur + 1 });
    }
    if (skipDeparts && nextStop) onTravel(nextStop, 'fwd');
    else go(cur + 1);
  }, [skipDeparts, nextStop, onTravel, go, cur, picks, line.slug]);

  // One `view` per ticket shown, with how long it stayed up.
  const shown = picks[cur];
  useEffect(() => {
    if (!shown) return;
    beginView({ media_id: shown.media_id, rank: shown.rank, line: line.slug, position: cur + 1 });
    return endView;
  }, [shown, line.slug, cur]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const forward = e.key === 'ArrowRight';
      if (e.shiftKey) {
        const to = forward ? nextStop : prevStop;
        if (to) onTravel(to, forward ? 'fwd' : 'back');
      } else {
        go(cur + (forward ? 1 : -1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cur, go, nextStop, prevStop, onTravel]);

  const touch = useRef<{ x: number; y: number } | null>(null);

  const [stamp, setStamp] = useState(clockText);
  useEffect(() => {
    const t = window.setInterval(() => setStamp(clockText()), 15000);
    return () => window.clearInterval(t);
  }, []);

  const rec: Recommendation | undefined = picks[cur];
  if (!rec) return null;

  const link = titleLink(rec, source);
  const openedLink = () =>
    trackFeedback({ type: 'open', media_id: rec.media_id, rank: rec.rank, line: line.slug,
      position: cur + 1 });

  return (
    <>
      <Scenery scene={scene} />

      <header className="hud">
        <div>
          <span className="kanji">{line.kanji}</span>
          <span className="en">
            {line.lineEn} ・ {line.genreEn}
          </span>
        </div>
        <span className="grow" />
        <nav className="route" aria-label="Your lines">
          {network.map((s, i) => (
            <RouteDot
              key={s.line.slug}
              first={i === 0}
              state={dotState(i, index)}
              stop={s}
              onRide={i === index ? null : () => onTravel(s, i > index ? 'fwd' : 'back')}
            />
          ))}
        </nav>
        {/*
          The concept read "PLATFORM 01 / 9" because its nine lines were always
          all nine. A rider's network is a subset, so the platform number and
          the position in the network are no longer the same figure — printing
          them as one fraction gives you "PLATFORM 09 / 8". The route dots to
          the left already show position; this shows the platform.
        */}
        <span className="idx">PLATFORM {line.id}</span>
      </header>

      <main className="hall" ref={refs.hall}>
        <div className="machine" ref={refs.machine}>
          <span className="screw" style={{ left: 7, top: 9 }} />
          <span className="screw" style={{ right: 7, top: 9 }} />
          <span className="screw" style={{ left: 7, bottom: 7 }} />
          <span className="screw" style={{ right: 7, bottom: 7 }} />

          <div className="led">
            <span className="brand">
              次 <Wordmark />
            </span>
            <div className="marquee">
              <span>
                {line.kanji} {line.lineEn} ・ {line.genreJp} {line.genreEn} ・ {picks.length}{' '}
                {picks.length === 1 ? 'TITLE' : 'TITLES'} AT THIS PLATFORM ・ ‹ › TO WALK THE
                PLATFORM ・ SHIFT + › FOR THE NEXT STATION ・
              </span>
            </div>
          </div>

          <div className="bay">
            <div className="baylabel">
              <span>TICKET / 券</span>
              <span>{stamp}</span>
            </div>
            <div className="slot" aria-hidden="true" />
            <div
              id="ticketHolder"
              ref={refs.holder}
              aria-live="polite"
              onTouchStart={(e) => {
                const t = e.touches[0];
                touch.current = t ? { x: t.clientX, y: t.clientY } : null;
              }}
              onTouchEnd={(e) => {
                const start = touch.current;
                const end = e.changedTouches[0];
                touch.current = null;
                if (!start || !end) return;
                const dx = end.clientX - start.x;
                const dy = end.clientY - start.y;
                if (Math.abs(dx) > 52 && Math.abs(dx) > Math.abs(dy)) go(cur + (dx < 0 ? 1 : -1));
              }}
            >
              <div id="ticketScale" ref={refs.stage}>
                <Ticket
                  key={`${line.slug}:${cur}`}
                  rec={rec}
                  line={line}
                  position={cur + 1}
                  total={picks.length}
                  leaving={leaving}
                  source={source}
                />
              </div>
            </div>
          </div>

          <div className="console">
            <div className="controls">
              <Dial slug={line.slug} n={picks.length} cur={cur} seen={seen} />
              <div className="railwrap">
                <div className="stubs">
                  {picks.map((p, i) => (
                    <button
                      key={p.media_id}
                      className={[i === cur ? 'on' : '', seen.has(i) && i !== cur ? 'seen' : '']
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => go(i)}
                      aria-label={p.title_english ?? p.title_romaji ?? `#${p.media_id}`}
                      title={p.title_english ?? p.title_romaji ?? undefined}
                    />
                  ))}
                </div>
                <div className="counter">
                  <b>{cur + 1}</b> / {picks.length} ・ {line.genreJp}の全{picks.length}件
                </div>
              </div>
            </div>

            <div className="acts">
              <a
                className="act buy"
                href={link.href}
                target="_blank"
                rel="noreferrer noopener"
                onClick={openedLink}
                onAuxClick={openedLink}
              >
                詳細 {link.label}
              </a>
              <button className="act skip" onClick={skip}>
                次へ NEXT{skipDeparts ? ' ▶' : ''}
              </button>
            </div>
          </div>
        </div>
      </main>

      <nav className="depart">
        <button
          className="dep prev"
          onClick={() => prevStop && onTravel(prevStop, 'back')}
          disabled={!prevStop}
          style={prevStop ? { ['--dc' as string]: prevStop.line.color } : undefined}
        >
          <span className="lbl">◀ 前の駅 ・ PREV STOP</span>
          <DepName stop={prevStop} />
        </button>
        <button className="dep map" onClick={onMap}>
          路線図
          <br />
          MAP
        </button>
        <button
          className="dep next"
          onClick={() => nextStop && onTravel(nextStop, 'fwd')}
          disabled={!nextStop}
          style={nextStop ? { ['--dc' as string]: nextStop.line.color } : undefined}
        >
          <span className="lbl">次の駅 ・ NEXT STOP ▶</span>
          <DepName stop={nextStop} />
        </button>
      </nav>
    </>
  );
}

/** The neighbouring station's name as a platform sign: number, kanji, line. */
function DepName({ stop }: { stop: StationStop | null }) {
  if (!stop) return <span className="nm">—</span>;
  return (
    <span className="nm">
      <i className="pl">{stop.line.id}</i>
      {stop.line.kanji}
      <small>{stop.line.lineEn}</small>
    </span>
  );
}

function dotState(i: number, current: number): string {
  if (i === current) return 'on';
  return i < current ? 'past' : '';
}

/** A stop on the rider's route; pressing one rides there. */
function RouteDot({
  first,
  state,
  stop,
  onRide,
}: {
  first: boolean;
  state: string;
  stop: StationStop;
  onRide: (() => void) | null;
}) {
  const name = `${stop.line.kanji} ${stop.line.lineEn}`;
  return (
    <>
      {!first && <s />}
      <button
        className="dot"
        onClick={onRide ?? undefined}
        aria-label={name}
        aria-current={onRide ? undefined : 'location'}
        title={name}
      >
        <b className={state} style={{ ['--c' as string]: stop.line.color }} />
      </button>
    </>
  );
}

function clockText(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export type { Line };
